import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceStore } from '../../../store/useServiceStore';
import { mapApiFieldErrors } from '../../../errors/mapFieldErrors';
import { Button } from '../../../components/Shared/Button';
import { FormField } from '../../../components/Shared/FormField';
import { TextArea } from '../../../components/Shared/TextArea';
import type {
  FacilityFlowCoreModulesServicesDtosServiceDto,
  FacilityFlowCoreModulesServicesWorkflowsCreateServiceRequest
} from '../../../api/generated/models';

interface ServiceFormProps {
  service?: FacilityFlowCoreModulesServicesDtosServiceDto;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ServiceForm({ service, onSuccess, onCancel }: ServiceFormProps) {
  const { t } = useTranslation(['services', 'common']);
  const { createService, updateService } = useServiceStore();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: service?.name || '',
    description: service?.description || '',
    unit: service?.unit || 'hour',
    basePrice: service?.basePrice || 0,
    estimatedDurationMinutes: service?.estimatedDurationMinutes || 0,
  });

  const NAME_MAX = 200;
  const DESCRIPTION_MAX = 4000;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = t('services:form.validation.nameRequired');
    } else if (formData.name.length > NAME_MAX) {
      newErrors.name = t('services:form.validation.nameMaxLength', { maxLength: NAME_MAX });
    }
    if (formData.description.length > DESCRIPTION_MAX) {
      newErrors.description = t('services:form.validation.descriptionMaxLength', {
        maxLength: DESCRIPTION_MAX,
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);

    try {
      const data: FacilityFlowCoreModulesServicesWorkflowsCreateServiceRequest = {
        name: formData.name,
        description: formData.description,
        unit: formData.unit,
        basePrice: formData.basePrice,
        estimatedDurationMinutes: formData.estimatedDurationMinutes,
      };

      if (service) {
        await updateService(service.id!, data);
      } else {
        await createService(data);
      }

      onSuccess();
    } catch (error) {
      setErrors(mapApiFieldErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-4">
        <FormField
          label={t('services:form.nameLabel')}
          required
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={t('services:form.namePlaceholder')}
          error={errors.name}
        />

        <TextArea
          label={t('services:form.descriptionLabel')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={t('services:form.descriptionPlaceholder')}
          rows={4}
          maxLength={DESCRIPTION_MAX}
          error={errors.description}
          helperText={t('services:form.descriptionCounter', {
            current: formData.description.length,
            maxLength: DESCRIPTION_MAX,
          })}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('services:form.unitLabel')} required>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value as 'hour' | 'sqm' | 'piece' })}
              required
              aria-label={t('services:form.unitAriaLabel')}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            >
              <option value="hour">{t('services:units.hour')}</option>
              <option value="sqm">{t('services:units.sqm')}</option>
              <option value="piece">{t('services:units.piece')}</option>
            </select>
          </FormField>

          <FormField
            label={t('services:form.basePriceLabel')}
            required
            type="number"
            step="0.01"
            min="0"
            value={formData.basePrice}
            onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) || 0 })}
            placeholder={t('services:form.basePricePlaceholder')}
            className="tabular-nums"
          />
        </div>

        <FormField
          label={t('services:form.estimatedDurationLabel')}
          required
          type="number"
          min="0"
          value={formData.estimatedDurationMinutes}
          onChange={(e) => setFormData({ ...formData, estimatedDurationMinutes: parseInt(e.target.value) || 0 })}
          placeholder={t('services:form.estimatedDurationPlaceholder')}
          className="tabular-nums"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-slate-100">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
          className="w-full sm:w-auto"
        >
          {t('services:buttons.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="w-full sm:w-auto"
        >
          {loading ? t('services:buttons.saving') : service ? t('services:buttons.update') : t('services:buttons.create')}
        </Button>
      </div>
    </form>
  );
}
